import { BaseCampaignItem, CampaignBase } from '@retail/campaign/types';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { Box } from '@mui/material';
import { dataGridCellNoFocusOutlineClass } from '@shared/styles';
import clsx from 'clsx';
import {
  columnsManagementSlotProps,
  DATA_GRID_ID,
  getDataGridLocalization,
  getInitialHiddenDataGridColumnsFromLocalStorage,
  MgPrisLocale,
  saveHiddenDataGridColumnsToLocalStorage,
} from '@retail/utils';
import { getTogglableDataGridColumns } from '@shared/utils';
import React, { useMemo } from 'react';
import { CampaignActions } from '../CampaignActions';
import { useRowColorGrouping } from '@retail/hooks';

interface Props {
  campaigns: CampaignBase<BaseCampaignItem>[];
  columns: GridColDef[];
  language: string;
  isLoading: boolean;
  deleteCampaign: (campaign: CampaignBase<BaseCampaignItem>) => void;
  getCampignHref: (campaign: CampaignBase<BaseCampaignItem>) => string;
}

type CampaignBaseGridColDef = GridColDef<CampaignBase<BaseCampaignItem>>;

const baseColumnProps: Partial<CampaignBaseGridColDef> = {
  align: 'left',
  headerAlign: 'left',
  headerName: '',
  disableColumnMenu: true,
};

export const CampaignsTable = ({
  campaigns,
  columns,
  language,
  isLoading,
  deleteCampaign,
  getCampignHref,
}: Props) => {
  const { getRowColorForStripedTable } = useRowColorGrouping();
  const actionsColumn: CampaignBaseGridColDef = {
    ...baseColumnProps,
    field: 'actions',
    headerName: '',
    width: 95,
    align: 'center',
    renderCell: ({ row }) => (
      <CampaignActions
        campaignHref={getCampignHref(row)}
        deleteCampaign={
          row.isCampaignDeletable()
            ? () => {
                deleteCampaign(row);
              }
            : undefined
        }
      />
    ),
  };

  // @ts-ignore
  const leftHeaderIsEmpty = useMemo(() => columns[0].renderHeader?.() === null, [columns]);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          getRowId={(row) => row.id}
          rows={campaigns}
          columns={[...columns, actionsColumn]}
          getRowClassName={getRowColorForStripedTable}
          rowHeight={65}
          getCellClassName={() => clsx(dataGridCellNoFocusOutlineClass)}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          hideFooter
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          loading={isLoading}
          onColumnVisibilityModelChange={(_, details) => {
            const toggableColumns = getTogglableDataGridColumns(columns);

            saveHiddenDataGridColumnsToLocalStorage(
              DATA_GRID_ID.CAMPAIGN_TABLE,
              toggableColumns,
              details.api.state.columns.columnVisibilityModel
            );
          }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                ...getInitialHiddenDataGridColumnsFromLocalStorage(
                  DATA_GRID_ID.CAMPAIGN_TABLE,
                  getTogglableDataGridColumns(columns)
                ),
              },
            },
            pinnedColumns: { right: ['actions'] },
          }}
          slotProps={{
            ...columnsManagementSlotProps,
          }}
          sx={{
            '& .MuiDataGrid-row, .MuiDataGrid-topContainer': leftHeaderIsEmpty
              ? {
                  paddingLeft: 0,
                }
              : null,
          }}
        />
      </Box>
    </Box>
  );
};
